import { Box, Button, Menu, MenuItem, Typography } from "@mui/material";
import { useState } from "react";
import FilterOffIcon from "@mui/icons-material/FilterAltOutlined";
import FilterOnIcon from "@mui/icons-material/FilterAlt";
import ActivityFilterStyles from "./ActivityFilterStyles";
import { useTranslation } from "react-i18next";

interface ActivityFilterProps<T extends string | number> {
  items: T[]; // Dynamic list of items to filter
  onFilterChange: (selectedItem: T | null) => void; // Callback for selected item
  label?: string; // Label for the dropdown
  defaultOptionLabel?: string; // Label for "All" option
}

const ActivityFilter = <T extends string | number>({
  items,
  onFilterChange,
  label = "Filter", // Default dropdown label
  defaultOptionLabel = "All Terms", // Default "All" option label
}: ActivityFilterProps<T>) => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [selectedItem, setSelectedItem] = useState<T | null>(null);

  const { t } = useTranslation();

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleItemClick = (item: T | null) => {
    setSelectedItem(item);
    onFilterChange(item);
    setAnchorEl(null); // Close the menu after selection
  };

  const open = Boolean(anchorEl);

  return (
    <Box 
      sx={{ display: "flex", justifyContent: "flex-end" }}
      id="activityfilter_container"
      data-test-id="activityfilter_container"
      aria-label="Activity filter container"
    >
      <Button
        sx={{
          boxShadow: "none",
          ":hover": {
            background: "none",
            color: "#000000",
          },
        }}
        onClick={handleClick}
        id="activityfilter_button"
        data-test-id="activityfilter_button"
        aria-label={selectedItem ? `Filter active: ${selectedItem}` : "Show filter options"}
      >
        <Box 
          sx={ActivityFilterStyles.tableHeadingFilterImage}
          id="activityfilter_icon"
          data-test-id="activityfilter_icon"
          aria-label={selectedItem ? "Filter is active" : "Filter is inactive"}
        >
          {selectedItem ? <FilterOnIcon /> : <FilterOffIcon />}
        </Box>
        <Typography 
          sx={ActivityFilterStyles.tableHeadingFilterText}
          id="activityfilter_text"
          data-test-id="activityfilter_text"
          aria-label={selectedItem ? `Showing only ${selectedItem}` : "Showing all terms"}
        >
          {selectedItem
            ? `${t("Showing only")} ${selectedItem}`
            : t("Showing All Terms")}
        </Typography>
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        id="activityfilter_menu"
        data-test-id="activityfilter_menu"
        aria-label="Filter options menu"
      >
        <MenuItem
          onClick={() => handleItemClick(null)}
          value="all"
          selected={null === selectedItem}
          id="activityfilter_all_option"
          data-test-id="activityfilter_all_option"
          aria-label="Show all terms"
        >
          {t(defaultOptionLabel)}
        </MenuItem>
        {items.map((item) => (
          <MenuItem
            key={item}
            onClick={() => handleItemClick(item)}
            selected={item === selectedItem}
            id={`activityfilter_option_${item}`}
            data-test-id={`activityfilter_option_${item}`}
            aria-label={`Filter by ${item}`}
          >
            {t(String(item))}
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
};

export default ActivityFilter;
